/* External */
import {
  memo, useEffect, useRef, useMemo,
} from 'react';
import { useSelector } from 'react-redux';

/* Components */
import LanderPage from '../../../components/LanderPage';
import GranHermanoModal from '../../../components/GranHermanoModal';
import SkyMaisModal from '../../../components/SkyMaisModal';
import TapppModal from '../../../components/TapppModal';

/* Utils */
import {
  PageType,
} from '../../../utils/constants';
import { useGtmLocalContext } from '../../../providers/GtmLocalProvider';
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../utils/googleTags';
import { useCountry, useIsBrazil } from '../../../providers/ConfigProvider';
import { useUserAccount } from '../../../providers/UserProvider';
import platform from '../../../utils/platform';
import {
  APP_THEME,
  IS_GRAN_HERMANO_WARNING_ACTIVE,
  IS_FIRST_WARNING_DGO_SKYMAIS_ACTIVE,
  IS_SECOND_WARNING_DGO_SKYMAIS_ACTIVE,
  IS_TAPPP_SDK_MODAL_ACTIVE,
} from '../../../utils/env';
import {
  isGranHermanoActiveForCountry,
  getProfileSelected,
  getProfilesAlreadyViewedGranHermanoModal,
  getProfilesAlreadyViewedTapppModal,
  getStoragedShowWarningSkyMais,
  getStoragedShowTapppModal,
  isTapppPanelEnabledForUser,
} from '../../../utils/profiles';

/* Other */
import { selectShowModal } from '../../../storage/modal/selectors';

function Home() {
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const { uid: userId, iso2Code } = useUserAccount() || {};
  const country = useCountry();
  const isBrazil = useIsBrazil();
  const shouldShowModal = useSelector(selectShowModal);
  const isStorageShowWarningActive = useMemo(
    getStoragedShowWarningSkyMais,
    [shouldShowModal],
  );
  const isStoragedShowTapppModalActive = useMemo(
    getStoragedShowTapppModal,
    [shouldShowModal],
  );

  const profileSelected = useMemo(getProfileSelected, []);
  const { isBetAllowed } = profileSelected?.interactivities || {};
  const profilesAlreadySeenGranHermanoModal = useMemo(
    getProfilesAlreadyViewedGranHermanoModal,
    [shouldShowModal],
  );
  const profilesAlreadySeenTapppModal = useMemo(
    getProfilesAlreadyViewedTapppModal,
    [shouldShowModal],
  );

  const profileStoraged = profilesAlreadySeenTapppModal.find(
    (profile) => profile.profile === profileSelected.id,
  );

  const isProfileEnabledForTapppModal = IS_TAPPP_SDK_MODAL_ACTIVE
    && isTapppPanelEnabledForUser(iso2Code, userId) && isBetAllowed;

  const profileInteractionWithTapppModal = !profileStoraged?.viewedModal
    && profileStoraged?.dismissedModal < 3;

  const showTapppModal = isProfileEnabledForTapppModal
    && (profileStoraged ? profileInteractionWithTapppModal : true)
    && isStoragedShowTapppModalActive;

  const showGranHermanoModal = IS_GRAN_HERMANO_WARNING_ACTIVE
    && isGranHermanoActiveForCountry(iso2Code)
    && !profilesAlreadySeenGranHermanoModal.includes(profileSelected?.id)
    && !isProfileEnabledForTapppModal;

  const showSkyMaisDefaultModal = (
    IS_FIRST_WARNING_DGO_SKYMAIS_ACTIVE
    || IS_SECOND_WARNING_DGO_SKYMAIS_ACTIVE)
    && isBrazil && APP_THEME === 'dgo'
    && isStorageShowWarningActive;

  const showSkyMaisTizenModal = IS_SECOND_WARNING_DGO_SKYMAIS_ACTIVE
    && isBrazil && platform.isTizen && APP_THEME === 'skymais'
    && isStorageShowWarningActive;

  const showSkyMaisModal = showSkyMaisDefaultModal || showSkyMaisTizenModal;

  useEffect(() => {
    if (tagsMountRef.current && country) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.HOME_DATA,
        subCategory: GTM_SUBCATEGORIES.PAGES.HOME,
        eventName: GTM_EVENT_NAMES.PAGE_VIEW,
      });
      tagsMountRef.current = false;
    }
  }, [commonTaggingEvent, country]);

  return (
    <>
      { showGranHermanoModal && <GranHermanoModal /> }
      { showSkyMaisModal && <SkyMaisModal /> }
      { showTapppModal && <TapppModal /> }
      <LanderPage pageId={PageType.HOME} />
    </>
  );
}

export default memo(Home);
